/**
 * GraphQL Client for AMRTS Dashboard
 * Type-safe queries with TanStack Query hooks
 */

import { GraphQLClient } from 'graphql-request';
import { useQuery } from '@tanstack/react-query';

// GraphQL client instance
const apiUrl = import.meta.env.PROD
  ? 'https://amrts-trading-engine.finhub.workers.dev/graphql'
  : '/graphql';

const client = new GraphQLClient(apiUrl);

// Type definitions matching GraphQL schema
export interface Account {
  balance: number;
  cash: number;
  buyingPower: number;
  portfolioValue: number;
  dailyPnL: number;
  dailyPnLPct: number;
  weeklyPnL: number;
  monthlyPnL: number;
  openPositionsCount: number;
  portfolioHeat: number;
  lastUpdated: string;
}

export interface Position {
  id: string;
  symbol: string;
  entryPrice: number;
  currentPrice: number;
  quantity: number;
  pnl: number;
  pnlPct: number;
  sector: string | null;
  entryTime: string;
  entryReason: string | null;
  highestPrice: number | null;
  trailingStopActivated: boolean;
  trailingStopPct: number | null;
}

export interface CircuitBreakerLevel {
  level: number;
  name: string;
  triggered: boolean;
  currentValue: number | null;
  threshold: number | null;
  status: string;
}

export interface CircuitBreakers {
  triggered: boolean;
  highestLevelTriggered: number | null;
  action: string;
  reason: string | null;
  canTrade: boolean;
  canEnter: boolean;
  canExit: boolean;
  levels: CircuitBreakerLevel[];
}

export interface DailyPerformance {
  date: string;
  startingBalance: number;
  endingBalance: number;
  realizedPnL: number;
  unrealizedPnL: number;
  totalPnL: number;
  tradesCount: number;
  winsCount: number;
  lossesCount: number;
  winRate: number;
}

export interface PDTStatus {
  accountBalance: number;
  isAboveThreshold: boolean;
  tradingMode: string;
  dayTradesUsed: number;
  dayTradesRemaining: number;
  canDayTrade: boolean;
  warningMessage: string | null;
  windowStart: string;
  windowEnd: string;
}

export interface SectorMomentum {
  sector: string;
  etfTicker: string;
  dailyChangePct: number;
  weeklyChangePct: number | null;
  momentumScore: number;
  category: string;
  isRotatingOut: boolean;
}

export interface MarketData {
  vix: number;
  vixChange: number;
  spy: number;
  spyChange: number;
  spyChangePct: number;
  marketRegime: string;
  sectorRotation: SectorMomentum[];
  timestamp: string;
}

export interface ClosedTrade {
  id: string;
  symbol: string;
  entryTime: string;
  entryPrice: number;
  exitTime: string;
  exitPrice: number;
  quantity: number;
  pnl: number;
  pnlPct: number;
  holdDurationMinutes: number;
  sector: string | null;
  entryReason: string | null;
  washSale: boolean;
}

export interface TaxSummary {
  shortTermGains: number;
  shortTermLosses: number;
  longTermGains: number;
  longTermLosses: number;
  netShortTerm: number;
  netLongTerm: number;
  totalNetGainLoss: number;
  washSaleLossesDisallowed: number;
  activeWashSaleBlocks: number;
  ytdRealized: number;
}

export interface Activity {
  id: string;
  timestamp: string;
  activityType: string;
  severity: string;
  symbol: string | null;
  message: string;
  metadata: Record<string, unknown> | null;
  cycleId: string;
}

export interface NewsArticle {
  headline: string;
  summary: string;
  url: string;
  createdAt: string;
  sentiment: string;
  sentimentScore: number;
}

export interface NewsSentiment {
  symbol: string;
  overallSentiment: string;
  positiveCount: number;
  negativeCount: number;
  neutralCount: number;
  averageScore: number;
  blockedForTrade: boolean;
  reason: string | null;
  articles: NewsArticle[];
  lastUpdated: string;
}

// GraphQL Queries
const ACCOUNT_QUERY = /* GraphQL */ `
  query GetAccount {
    account {
      balance
      cash
      buyingPower
      portfolioValue
      dailyPnL
      dailyPnLPct
      weeklyPnL
      monthlyPnL
      openPositionsCount
      portfolioHeat
      lastUpdated
    }
  }
`;

const POSITIONS_QUERY = /* GraphQL */ `
  query GetPositions {
    positions {
      id
      symbol
      entryPrice
      currentPrice
      quantity
      pnl
      pnlPct
      sector
      entryTime
      entryReason
      highestPrice
      trailingStopActivated
      trailingStopPct
    }
  }
`;

const CIRCUIT_BREAKERS_QUERY = /* GraphQL */ `
  query GetCircuitBreakers {
    circuitBreakers {
      triggered
      highestLevelTriggered
      action
      reason
      canTrade
      canEnter
      canExit
      levels {
        level
        name
        triggered
        currentValue
        threshold
        status
      }
    }
  }
`;

const PERFORMANCE_QUERY = /* GraphQL */ `
  query GetPerformance($days: Int) {
    performance(days: $days) {
      date
      startingBalance
      endingBalance
      realizedPnL
      unrealizedPnL
      totalPnL
      tradesCount
      winsCount
      lossesCount
      winRate
    }
  }
`;

const PDT_STATUS_QUERY = /* GraphQL */ `
  query GetPDTStatus {
    pdtStatus {
      accountBalance
      isAboveThreshold
      tradingMode
      dayTradesUsed
      dayTradesRemaining
      canDayTrade
      warningMessage
      windowStart
      windowEnd
    }
  }
`;

const MARKET_DATA_QUERY = /* GraphQL */ `
  query GetMarketData {
    marketData {
      vix
      vixChange
      spy
      spyChange
      spyChangePct
      marketRegime
      sectorRotation {
        sector
        etfTicker
        dailyChangePct
        weeklyChangePct
        momentumScore
        category
        isRotatingOut
      }
      timestamp
    }
  }
`;

const RECENT_TRADES_QUERY = /* GraphQL */ `
  query GetRecentTrades($limit: Int) {
    recentTrades(limit: $limit) {
      id
      symbol
      entryTime
      entryPrice
      exitTime
      exitPrice
      quantity
      pnl
      pnlPct
      holdDurationMinutes
      sector
      entryReason
      washSale
    }
  }
`;

const TAX_SUMMARY_QUERY = /* GraphQL */ `
  query GetTaxSummary($year: Int) {
    taxSummary(year: $year) {
      shortTermGains
      shortTermLosses
      longTermGains
      longTermLosses
      netShortTerm
      netLongTerm
      totalNetGainLoss
      washSaleLossesDisallowed
      activeWashSaleBlocks
      ytdRealized
    }
  }
`;

const ACTIVITY_FEED_QUERY = /* GraphQL */ `
  query GetActivityFeed($limit: Int) {
    activityFeed(limit: $limit) {
      id
      timestamp
      activityType
      severity
      symbol
      message
      metadata
      cycleId
    }
  }
`;

const NEWS_SENTIMENT_QUERY = /* GraphQL */ `
  query GetNewsSentiment($symbol: String!) {
    newsSentiment(symbol: $symbol) {
      symbol
      overallSentiment
      positiveCount
      negativeCount
      neutralCount
      averageScore
      blockedForTrade
      reason
      articles {
        headline
        summary
        url
        createdAt
        sentiment
        sentimentScore
      }
      lastUpdated
    }
  }
`;

// Custom hooks for data fetching
export function useAccount() {
  return useQuery<{ account: Account }>({
    queryKey: ['account'],
    queryFn: () => client.request(ACCOUNT_QUERY),
  });
}

export function usePositions() {
  return useQuery<{ positions: Position[] }>({
    queryKey: ['positions'],
    queryFn: () => client.request(POSITIONS_QUERY),
  });
}

export function useCircuitBreakers() {
  return useQuery<{ circuitBreakers: CircuitBreakers }>({
    queryKey: ['circuitBreakers'],
    queryFn: () => client.request(CIRCUIT_BREAKERS_QUERY),
  });
}

export function usePerformance(days = 30) {
  return useQuery<{ performance: DailyPerformance[] }>({
    queryKey: ['performance', days],
    queryFn: () => client.request(PERFORMANCE_QUERY, { days }),
  });
}

export function usePDTStatus() {
  return useQuery<{ pdtStatus: PDTStatus }>({
    queryKey: ['pdtStatus'],
    queryFn: () => client.request(PDT_STATUS_QUERY),
  });
}

export function useMarketData() {
  return useQuery<{ marketData: MarketData }>({
    queryKey: ['marketData'],
    queryFn: () => client.request(MARKET_DATA_QUERY),
  });
}

export function useRecentTrades(limit = 20) {
  return useQuery<{ recentTrades: ClosedTrade[] }>({
    queryKey: ['recentTrades', limit],
    queryFn: () => client.request(RECENT_TRADES_QUERY, { limit }),
  });
}

export function useTaxSummary(year?: number) {
  return useQuery<{ taxSummary: TaxSummary }>({
    queryKey: ['taxSummary', year],
    queryFn: () => client.request(TAX_SUMMARY_QUERY, { year }),
  });
}

export function useActivityFeed(limit = 50) {
  return useQuery<{ activityFeed: Activity[] }>({
    queryKey: ['activityFeed', limit],
    queryFn: () => client.request(ACTIVITY_FEED_QUERY, { limit }),
    refetchInterval: 5000, // Poll every 5 seconds
    refetchIntervalInBackground: true,
  });
}

export function useNewsSentiment(symbol: string) {
  return useQuery<{ newsSentiment: NewsSentiment }>({
    queryKey: ['newsSentiment', symbol],
    queryFn: () => client.request(NEWS_SENTIMENT_QUERY, { symbol }),
    enabled: !!symbol, // Only fetch if symbol is provided
    refetchInterval: 300000, // Refetch every 5 minutes (cached on backend for 1 hour)
  });
}
